import _ from 'lodash';
import Background from './Background';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import ClickableMixin from './mixins/ClickableMixin';
import AnimatableMixin from './mixins/AnimatableMixin';
import mixin from '../privates/utils/mixin';
import componentsBatchFunctions from '../privates/utils/componentsBatchFunctions';
import ContainableMixin from './mixins/ContainableMixin';
import * as members from '../privates/privateMembers';
import {types} from '../privates/utils';

/**
 * @class ColumnStrip
 * @summary A `ColumnStrip` is a strip of `Column` elements.
 * @mixes $w.Element
 * @mixes $w.Background
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.ClickableMixin
 * @mixes $w.ContainableMixin
 * @memberof $w
 * @tagname columnstrip
 * @viewername wysiwyg.viewer.components.StripColumnsContainer
 * @definitionId wysiwyg.viewer.components.StripColumnsContainer
 */

class ColumnStrip extends Background {

    /**
     * @member columns
     * @label property
     * @syntax
       * get columns(): Column[]
     * @summary Gets a list of all the columns contained in the column strip.
     * @description
     *  Note that the column indices in a column strip are zero-based, even though
     *  they are numbered starting from 1 in the Editor.
     * @snippet [ColumnStrip-columns.es6=Get a column strip's columns]
     * @snippet [ColumnStrip-columns_plus.es6=Get the columns, number of columns, and ID of the first column from a column strip]
     * @type {$w.Column[]}
     * @memberof $w.ColumnStrip
     * @instance
     * @readonly
     */
    get columns() {
        const RMI = members._RMI.get(this);
        const wixSelector = members._wixSelector.get(this);
        const compId = members._compId.get(this);
        const controllerId = members._controllerId.get(this);

        const columns = RMI.getChildren(compId)
            .filter((id) => _.includes(types.getPotentialViewerTypes('Column'), RMI.getComp(id).type))
            .map((id) => wixSelector.getInstance(RMI, RMI.getComp(id).type, id, controllerId));

        return componentsBatchFunctions.expose(columns);
    }

    /**
     * @member background
     * @label property
     * @syntax
     * get background(): BackgroundOptions
     * @summary Gets an object containing information about the column strip's background, such as its image or video source.
     * @type {$w.Document~BackgroundOptions}
     * @snippet [Background-background_get.es6=Get the background information]
     * @snippet [Background-background_set_wix.es6=Set the background image to be an image from the Media Manager]
     * @snippet [Background-background_set_web.es6=Set the background image to be an image from the web]
     * @memberof $w.ColumnStrip
     * @readonly
     */
    get background() {
        if (this.columns.length === 1) {
            return _.head(this.columns).background;
        }

        return super.background;
    }
}

mixin(ColumnStrip.prototype, HiddenCollapsedMixin.prototype);
mixin(ColumnStrip.prototype, ClickableMixin.prototype);
mixin(ColumnStrip.prototype, ContainableMixin.prototype);
mixin(ColumnStrip.prototype, AnimatableMixin.prototype);

export default ColumnStrip;
